import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ProductShell, Button } from '@cloudgenie/ui';
import { API_BASE } from '../AuthContext';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(typeof data?.detail === 'string' ? data.detail : 'Reset failed. The link may be expired.');
        return;
      }
      setOk(true);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <ProductShell product="id" title="Reset link invalid" subtitle="No reset token provided.">
        <div className="max-w-sm mx-auto text-center">
          <Link to="/forgot-password"><Button variant="outline">Request a new link</Button></Link>
        </div>
      </ProductShell>
    );
  }

  if (ok) {
    return (
      <ProductShell product="id" title="Password updated" subtitle="You can now sign in with your new password.">
        <div className="max-w-sm mx-auto text-center">
          <Link to="/login" data-testid="link-post-reset-login"><Button variant="primary">Sign in</Button></Link>
        </div>
      </ProductShell>
    );
  }

  return (
    <ProductShell product="id" title="Set a new password" subtitle="Choose a strong password (8+ characters).">
      <form onSubmit={submit} className="max-w-sm mx-auto space-y-4" data-testid="reset-form">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="password">New password</label>
          <input id="password" data-testid="reset-password" type="password" required minLength={8} maxLength={128}
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </div>
        {error && <p data-testid="reset-error" className="text-sm text-red-500">{error}</p>}
        <Button variant="primary" type="submit" disabled={busy || password.length < 8} data-testid="reset-submit">
          {busy ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </ProductShell>
  );
}
